import { Controller } from "@hotwired/stimulus"
import { formatRatio, luminance, minContrast, nearestPassing } from "../lib/color_contrast"
import { backgroundsOf, colorOf as colorOfField, valueOf as valueOfField } from "../lib/color_fields"

const ACCENT = "accent_color"

// The Web Chat Accessibility section: lists the warnings contrast-check shows under the pickers, each with a
// suggested colour and "Use this", plus "Use accessible colours" for all of them. Re-renders on
// contrast-check:checked.
export default class extends Controller {
  static targets = ["status", "okNote", "warnNote", "title", "announcement", "fixAll", "list", "row", "cssNote"]

  connect() {
    this.render()
  }

  // Custom CSS is typed, not committed, so the note follows its `input` events.
  cssChanged(event) {
    if (event.target === this.customCssInput) this.renderCssNote()
  }

  render() {
    const issues = this.colourIssues()
    const ok = issues.length === 0
    this.okNoteTarget.classList.toggle("hidden", !ok)
    this.warnNoteTarget.classList.toggle("hidden", ok)
    this.fixAllTarget.classList.toggle("hidden", ok)
    if (!ok) this.titleTarget.textContent = `${issues.length} ${issues.length === 1 ? "colour needs" : "colours need"} more contrast`
    // The live region: only a changed result is written, so it isn't read again after every check. A save's
    // morph empties it, so the result is read once after each save.
    const message = (ok ? this.okNoteTarget : this.titleTarget).textContent.trim()
    if (this.announcementTarget.textContent !== message) this.announcementTarget.textContent = message
    this.listTarget.replaceChildren(...issues.map((issue, index) => this.renderIssue(issue, index)))
    this.renderCssNote()
  }

  // Custom CSS isn't checked, so say so while there is some (comments alone don't count).
  renderCssNote() {
    const hasCustomCss = !!this.customCssInput?.value.replace(/\/\*[\s\S]*?\*\//g, "").trim()
    this.cssNoteTarget.classList.toggle("hidden", !hasCustomCss)
  }

  colourIssues() {
    return this.warnings()
      .filter((warning) => !warning.classList.contains("hidden"))
      .map((warning) => {
        const { contrastCheckField: field, contrastCheckMin: min } = warning.dataset
        const colour = this.colorOf(field)
        // Everything the colour is drawn on (it must read on each); the samples show it on the first.
        const backgrounds = backgroundsOf(warning, (attribute) => this.colorOf(attribute))
        const suggestion = nearestPassing(colour, backgrounds, Number(min))
        if (!suggestion) return null
        const named = suggestion === "#000000" ? "black" : suggestion === "#ffffff" ? "white" : suggestion.toUpperCase()
        const shade = !named.startsWith("#") ? "" : luminance(suggestion) < luminance(colour) ? ", a darker shade" : ", a lighter shade"
        return {
          field,
          suggestion,
          label: this.labelOf(field),
          detail: `Contrast ${formatRatio(minContrast(colour, backgrounds))}, needs ${min}:1. Try ${named}${shade}.`,
          now: { bg: backgrounds[0], fg: colour },
          next: { bg: backgrounds[0], fg: suggestion },
        }
      })
      .filter(Boolean)
  }

  renderIssue({ field, suggestion, label, detail, now, next }, index) {
    const row = this.rowTarget.content.firstElementChild.cloneNode(true)
    for (const [key, colours] of [["now", now], ["next", next]]) {
      const chip = row.querySelector(`[data-sample='${key}']`)
      chip.style.background = colours.bg
      chip.style.color = colours.fg
    }
    row.querySelector("[data-field='label']").textContent = label
    row.querySelector("[data-field='detail']").textContent = detail
    const button = row.querySelector("button")
    // Starts with the visible "Use this", so voice control users can say what they see (WCAG 2.5.3).
    button.setAttribute("aria-label", `Use this: ${suggestion.toUpperCase()} for ${label}`)
    button.addEventListener("click", () => this.apply(field, suggestion, index))
    return row
  }

  // "Use this".
  apply(attribute, hex, index) {
    this.fix(attribute, hex)
    // The clicked row is gone: focus the row that took its place, else the status line.
    const buttons = this.listTarget.querySelectorAll("button")
    const next = buttons[Math.min(index, buttons.length - 1)]
    if (next) next.focus()
    else this.statusTarget.focus()
  }

  // "Use accessible colours": every suggestion at once. One pass is enough: no colour that gets fixed is
  // another's background.
  fixAll() {
    this.colourIssues().forEach(({ field, suggestion }) => this.fix(field, suggestion))
    this.statusTarget.focus()
  }

  // A fix for the text on a blank accent fills in the accent it was worked out against: once the channel sets
  // a colour it stops inheriting the global accent, which would then fall back to the widget's stock one.
  fix(attribute, hex) {
    const against = this.warnings().find((warning) => warning.dataset.contrastCheckField === attribute)
      ?.dataset.contrastCheckAgainst ?? ""
    if (against.split(" ").includes(ACCENT) && !this.valueOf(ACCENT)) this.type(ACCENT, this.colorOf(ACCENT))
    this.type(attribute, hex)
  }

  // Types and commits the colour the way a merchant would, so the check and the preview follow; opens a closed
  // section first, so the change is visible.
  type(attribute, hex) {
    const text = this.textInput(attribute)
    if (!text) return
    const details = text.closest("details")
    if (details) details.open = true
    text.value = hex.toUpperCase()
    text.dispatchEvent(new Event("input", { bubbles: true }))
    text.dispatchEvent(new Event("change", { bubbles: true }))
  }

  warnings() {
    return [...(this.section?.querySelectorAll("[data-contrast-check-target='warning']") ?? [])]
  }

  // The element contrast-check runs on: the warnings and fields it checks are all inside it.
  get section() {
    return this.element.closest("[data-controller~='contrast-check']")
  }

  get customCssInput() {
    return this.section?.querySelector("textarea[name*='custom_css']")
  }

  fieldRoot(attribute) {
    return this.section?.querySelector(`[data-color-attribute="${attribute}"]`)
  }

  textInput(attribute) {
    return this.fieldRoot(attribute)?.querySelector("[data-color-field-target='text']")
  }

  labelOf(attribute) {
    return this.fieldRoot(attribute)?.querySelector("label")?.textContent.trim() || attribute
  }

  valueOf(attribute) {
    return valueOfField(this.fieldRoot(attribute))
  }

  colorOf(attribute) {
    return colorOfField(this.fieldRoot(attribute))
  }
}
